import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  MessageCircle,
  Link2,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

const steps = [
  {
    title: "Choose Your Job",
    desc: "Select the kind of work you want like maid, driver, cook, nurse, and more.",
    icon: Search,
    tone: "sky",
  },
  {
    title: "Contact Us Easily",
    desc: "Apply through WhatsApp, call us directly, or use the quick enquiry form.",
    icon: MessageCircle,
    tone: "orange",
  },
  {
    title: "Complete Proper Verification",
    desc: "We perform a proper verification process before connecting you with employers.",
    icon: ShieldCheck,
    tone: "indigo",
  },
  {
    title: "We Connect With You",
    desc: "Our team reviews your enquiry and connects you with suitable work.",
    icon: Link2,
    tone: "sky",
  },
];

const toneStyles = {
  sky: {
    iconWrap:
      "bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    chip: "bg-sky-50 text-sky-700 border-sky-100",
    glow: "from-sky-100/70 via-transparent to-cyan-100/50",
    line: "from-sky-200 via-sky-100 to-transparent",
  },
  orange: {
    iconWrap:
      "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    chip: "bg-orange-50 text-orange-700 border-orange-100",
    glow: "from-orange-100/70 via-transparent to-amber-100/50",
    line: "from-orange-200 via-orange-100 to-transparent",
  },
  indigo: {
    iconWrap:
      "bg-gradient-to-br from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    chip: "bg-indigo-50 text-indigo-700 border-indigo-100",
    glow: "from-indigo-100/70 via-transparent to-sky-100/50",
    line: "from-indigo-200 via-indigo-100 to-transparent",
  },
};

const JobsHowToApply = () => {
  return (
    <section className="relative px-4 py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#fffaf5_0%,#ffffff_48%,#f8fbff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(251,146,60,0.12),transparent_24%),radial-gradient(circle_at_82%_20%,rgba(56,189,248,0.10),transparent_24%)]" />

      {/* Section content */}
      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-orange-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-orange-700 font-medium mb-5 shadow-sm">
            Simple Process
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            How to Apply
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            No complicated process. Just follow these simple steps and contact us.
          </p>
        </motion.div>

        {/* GRID */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const tone = toneStyles[step.tone];

            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -4 }}
                className="group relative rounded-[28px] border border-white/70 bg-white/80 backdrop-blur-xl p-5 md:p-6 shadow-[0_12px_35px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition-all duration-300 flex flex-col h-full"
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow}`}
                />

                {/* Dotted art */}
                <div className="absolute top-3 right-3 opacity-[0.10]">
                  <svg width="58" height="58">
                    {Array.from({ length: 5 }).map((_, r) =>
                      Array.from({ length: 5 }).map((__, c) => (
                        <circle
                          key={`${r}-${c}`}
                          cx={7 + c * 11}
                          cy={7 + r * 11}
                          r="2"
                          fill="#0f172a"
                        />
                      ))
                    )}
                  </svg>
                </div>

                {/* TOP */}
                <div className="flex items-center justify-between mb-5">
                  <div
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center border ${tone.iconWrap}`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  <span
                    className={`inline-flex items-center justify-center min-w-[38px] h-9 px-3 rounded-full border text-sm font-semibold ${tone.chip}`}
                  >
                    {i + 1}
                  </span>
                </div>

                {/* CONTENT */}
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>

                <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed mb-5">
                  {step.desc}
                </p>

                {/* PUSH BOTTOM */}
                <div className="mt-auto flex items-center justify-between rounded-2xl border border-slate-100 bg-gradient-to-r from-white via-slate-50/80 to-white px-4 py-3">
                  <span className="text-sm text-gray-600">Step {i + 1}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600">
                    Continue
                    <Sparkles className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* VERIFIED STRIP */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mt-12 md:mt-14"
        >
          <div className="relative rounded-[28px] border border-white/70 bg-white/80 backdrop-blur-xl shadow px-5 py-5 md:px-6">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-green-50 border border-green-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>

              <div>
                <p className="text-sm font-semibold text-green-700 mb-1">
                  Verified & Safe Process
                </p>
                <p className="text-sm md:text-base text-gray-600">
                  We perform proper verification checks before connecting you with employers, ensuring a safer and more trusted experience.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default JobsHowToApply;